import axios from 'axios';
import { AuthResponse } from '../../auth';
import { OperatorInfo, Region, User } from './types';

/**
 * Модуль «Пользователи»: права и зону видимости проверяет сервер, флаги can* по каждому сотруднику
 * считает он же (PROJECT_NOTES §35.9-35.10). Фронтенд по флагам только скрывает или блокирует элементы.
 */
const USERS_API = '/api/v2/users';
const AUTH_API = '/api/v2/auth';
const BIOMETRICS_API = '/api/v2/biometrics';

/** Роль из справочника: assignable - ранг ниже ранга оператора (супер-администратору - любая) */
export interface AssignableRole {
  slug: string;
  name: string;
  level: number;
  badge: { bg: string; text: string; border: string } | null;
  targetInterface: 'admin' | 'dashboard';
  assignable: boolean;
}

/** Сессия сотрудника в карточке: токен сервер не отдает */
export interface UserSessionRow {
  id: number;
  ipAddress: string | null;
  deviceInfo: string | null;
  currentSection: string | null;
  lastActivityAt: string;
  createdAt: string;
  revokedAt?: string | null;
  revokedReason?: string | null;
  impersonatedBy?: number | null;
  isOnline: boolean;
  isRevoked?: boolean;
}

export interface BiometricsStatus {
  registered: boolean;
  descriptorCount: number;
}

export type ToggleKind = 'activation' | 'pos' | 'reports';

export interface PersonalDataInput {
  fullName: string;
  userphone: string;
  workPhone: string;
  /** Передается только с правом на ИИН: без него сервер поле не меняет */
  iin?: string;
}

interface MessageResponse {
  success?: boolean;
  message?: string;
}

function messageOf(res: { data?: MessageResponse }, fallback: string): string {
  return (res.data && res.data.message) || fallback;
}

/** Ответ 200 с success: false - отказ сервера, его текст показывается как ошибка */
function ensureSuccess(res: { data?: MessageResponse }, fallback: string): void {
  if (!res.data || !res.data.success) throw new Error(messageOf(res, fallback));
}

export async function fetchUsers(): Promise<{ users: User[]; operator: OperatorInfo | null }> {
  const res = await axios.get(USERS_API);
  if (!res.data || !res.data.success || !Array.isArray(res.data.users)) {
    throw new Error('Сервер вернул список пользователей в неожиданном виде');
  }
  return { users: res.data.users, operator: res.data.operator || null };
}

export async function fetchUserDetail(id: number): Promise<User> {
  const res = await axios.get(`${USERS_API}/${id}`);
  if (!res.data || !res.data.success || !res.data.user) throw new Error('Сервер вернул карточку сотрудника в неожиданном виде');
  return res.data.user;
}

export async function fetchRegions(): Promise<Region[]> {
  const res = await axios.get(`${USERS_API}/regions`);
  if (!res.data || !res.data.success || !Array.isArray(res.data.data)) throw new Error('Сервер вернул регионы в неожиданном виде');
  return res.data.data;
}

export async function fetchRoles(): Promise<AssignableRole[]> {
  const res = await axios.get(`${USERS_API}/roles`);
  if (!res.data || !res.data.success || !Array.isArray(res.data.data)) throw new Error('Сервер вернул роли в неожиданном виде');
  return res.data.data;
}

const TOGGLE_PATHS: Record<ToggleKind, string> = {
  activation: 'toggle-activation',
  pos: 'toggle-pos',
  reports: 'toggle-reports'
};

/** Новое значение флага (deactivation / pos / reports) задает targetStatus, сервер его и возвращает */
export async function toggleUserFlag(kind: ToggleKind, user: User, targetStatus: number): Promise<{ newStatus: number; message: string }> {
  const currentStatus = kind === 'activation' ? user.deactivation : kind === 'pos' ? user.pos : user.reports;
  const res = await axios.post(`${USERS_API}/${TOGGLE_PATHS[kind]}`, { userId: user.id, currentStatus, targetStatus });
  ensureSuccess(res, 'Сервер не подтвердил смену статуса');
  return { newStatus: Number(res.data.newStatus) ? 1 : 0, message: messageOf(res, 'Статус изменен') };
}

export async function massLock(kind: 'pos' | 'reports'): Promise<{ affectedRows: number; message: string }> {
  const res = await axios.post(`${USERS_API}/${kind === 'pos' ? 'mass-pos-lock' : 'mass-reports-lock'}`);
  ensureSuccess(res, 'Сервер не подтвердил массовую блокировку');
  return { affectedRows: Number(res.data.affectedRows) || 0, message: messageOf(res, 'Блокировка выполнена') };
}

export async function deleteUser(id: number): Promise<string> {
  const res = await axios.delete(`${USERS_API}/${id}`);
  ensureSuccess(res, 'Сервер не подтвердил удаление');
  return messageOf(res, 'Пользователь удален');
}

/** Системный ID меняет только ранг 100 (проверяет сервер) */
export async function changeUserId(id: number, newId: number): Promise<string> {
  const res = await axios.put(`${USERS_API}/${id}/user-id`, { newId });
  return messageOf(res, 'ID пользователя изменен');
}

/** Номер своей аптеки и/или курируемые аптеки: неуказанное поле не меняется */
export async function updatePharmacy(id: number, data: { pharmacyNum?: string; myPharmacy?: string }): Promise<string> {
  const res = await axios.put(`${USERS_API}/${id}/pharmacy`, data);
  return messageOf(res, 'Параметры аптеки обновлены');
}

export async function updateRole(id: number, role: string): Promise<string> {
  const res = await axios.put(`${USERS_API}/${id}/role`, { role });
  return messageOf(res, 'Роль обновлена');
}

/** null - очистить регион */
export async function updateRegion(id: number, cityId: number | null): Promise<string> {
  const res = await axios.put(`${USERS_API}/${id}/region`, { cityId });
  return messageOf(res, 'Регион обновлен');
}

export async function saveUserPersonalData(id: number, input: PersonalDataInput): Promise<string> {
  const res = await axios.put(`${USERS_API}/${id}/personal-data`, input);
  return messageOf(res, 'Персональные данные обновлены');
}

export async function uploadUserAvatar(id: number, dataUrl: string): Promise<string> {
  const res = await axios.post(`${USERS_API}/${id}/avatar`, { avatar: dataUrl });
  if (!res.data || !res.data.success || !res.data.avatarUrl) throw new Error('Сервер не подтвердил сохранение фото');
  return res.data.avatarUrl;
}

/** Пароль сотрудника без ввода текущего; свой пароль меняется только в профиле */
export async function resetUserPassword(id: number, newPassword: string): Promise<string> {
  const res = await axios.put(`${USERS_API}/${id}/password`, { newPassword });
  return messageOf(res, 'Пароль обновлен');
}

export async function fetchUserSessions(userId: number): Promise<UserSessionRow[]> {
  const res = await axios.get(`${AUTH_API}/sessions/${userId}`);
  if (!res.data || !res.data.success || !Array.isArray(res.data.sessions)) {
    throw new Error('Сервер вернул список сессий в неожиданном виде');
  }
  return res.data.sessions;
}

export async function revokeUserSession(sessionId: number): Promise<string> {
  const res = await axios.delete(`${AUTH_API}/sessions/${sessionId}`);
  return messageOf(res, 'Сессия завершена');
}

/** currentToken - своя текущая сессия, которую владелец оставляет; для чужих сессий сервер его не учитывает */
export async function revokeAllUserSessions(userId: number, currentToken: string | null): Promise<string> {
  const res = await axios.post(`${AUTH_API}/sessions/${userId}/revoke-all`, { currentToken });
  return messageOf(res, 'Сессии завершены');
}

/** Вход под сотрудником: сервер проверяет право и ранг, отдает сессию сотрудника и его интерфейс */
export async function impersonateUser(userId: number, adminToken: string): Promise<AuthResponse> {
  const res = await axios.post(`${AUTH_API}/impersonate/${userId}`, { adminToken });
  ensureSuccess(res, 'Не удалось войти под пользователем');
  return res.data;
}

export async function fetchBiometricsStatus(username: string): Promise<BiometricsStatus> {
  const res = await axios.get(`${BIOMETRICS_API}/status/${encodeURIComponent(username)}`);
  if (!res.data || !res.data.success) throw new Error('Сервер вернул статус биометрии в неожиданном виде');
  return { registered: Boolean(res.data.registered), descriptorCount: Number(res.data.descriptorCount) || 0 };
}

export async function resetUserBiometrics(userId: number): Promise<string> {
  const res = await axios.delete(`${USERS_API}/${userId}/biometrics`);
  return messageOf(res, 'Биометрия сброшена');
}
